import {
    type ViewStyle,
    StyleSheet
} from "react-native";
import type {
    ProgressColorsConstantType,
    ProgressDynamicStyleType,
    ProgressRingConstantType,
    ProgressSizeConstantType,
    ProgressMeasures,
    ProgressColors,
    ProgressRing,
    ProgressType,
    ProgressSize
} from "./type";
import {
    getTypeColors
} from "../../helpers/theme/typeColors";
import type {
    Mutable
} from "../../types";
import {
    getControlMeasures
} from "../../helpers/theme/control";
import {
    getStateColorKey
} from "../../helpers/theme/state";
import {
    webStyle
} from "../../utils";

export const PROGRESS_THICKNESS_RATIOS: Record<ProgressSize, number> = {
    small: 0.5,
    medium: 1,
    large: 2
};

export const PROGRESS_VALUE_COLOR_KEYS: Partial<Record<ProgressType, keyof NCoreUIKit.TextContentColors>> = {
    success: "success",
    danger: "danger"
};

export const PROGRESS_DESCRIPTION_COLOR_KEYS: Partial<Record<ProgressType, keyof NCoreUIKit.TextContentColors>> = {
    danger: "danger"
};

export const getProgressSize = ({
    controlPaddings,
    controlHeights,
    shapeConfigs,
    iconSizes,
    borders,
    spaces,
    size
}: ProgressSizeConstantType): ProgressMeasures => {
    const controlMeasures = getControlMeasures({
        controlPaddings,
        controlHeights,
        shapeConfigs,
        iconSizes,
        size
    });

    const ringDiameters: Record<ProgressSize, number> = {
        large: controlHeights.large + spaces.spacingSm,
        medium: controlHeights.small,
        small: iconSizes.large
    };

    const ringStrokes: Record<ProgressSize, number> = {
        large: borders.indicator,
        medium: borders.halo,
        small: borders.focus
    };

    return {
        thickness: borders.indicator * PROGRESS_THICKNESS_RATIOS[size],
        labelVariant: controlMeasures.labelVariant,
        diameter: ringDiameters[size],
        stroke: ringStrokes[size],
        iconSize: iconSizes.small
    };
};

export const getProgressRing = ({
    currentSize
}: ProgressRingConstantType): ProgressRing => {
    const radius = (currentSize.diameter - currentSize.stroke) / 2;

    return {
        circumference: 2 * Math.PI * radius,
        center: currentSize.diameter / 2,
        radius
    };
};

export const getProgressColors = ({
    colors,
    type
}: ProgressColorsConstantType): ProgressColors => {
    const typeColors = getTypeColors({
        variant: "filled",
        colors,
        type
    });

    return {
        descriptionColor: colors.content.text[PROGRESS_DESCRIPTION_COLOR_KEYS[type] ?? "mid"],
        valueColor: colors.content.text[PROGRESS_VALUE_COLOR_KEYS[type] ?? "mid"],
        trackColor: colors.system.state.overlay.fill.default,
        fadeColor: colors.system.high[getStateColorKey({
            type
        })],
        indicatorColor: typeColors.containerColor,
        ringValueColor: colors.content.text.high,
        titleColor: colors.content.text.high
    };
};

const stylesheet = StyleSheet.create({
    status: {
        pointerEvents: "none",
        position: "absolute",
        overflow: "hidden",
        ...webStyle({
            clipPath: "inset(50%)",
            whiteSpace: "nowrap"
        })
    },
    container: {
        width: "100%",
        minWidth: 0
    },
    ring: {
        flexShrink: 0
    }
});

export const useStyles = ({
    currentSize,
    borders,
    variant,
    spaces
}: ProgressDynamicStyleType) => {
    const styles = {
        container: {
            gap: spaces.spacingSm
        } as Mutable<ViewStyle>,
        status: {
            height: borders.line,
            width: borders.line
        } as Mutable<ViewStyle>
    };

    if(variant === "circular") {
        styles.container = {
            height: currentSize.diameter,
            width: currentSize.diameter
        };
    }

    return styles;
};
export default stylesheet;
